import { html, nothing, type PropertyDeclarations, type PropertyValues } from "lit";
import { LmaxBaseChartComponent } from "../../shared/components/lmax-base-chart-component";
import { fetchInstrumentCatalog } from "../../shared/services/InstrumentCatalogService";
import type { Instrument } from "../../shared/definitions/Instrument";
import { renderChart, type SpreadSeries } from "../chart";
import { fetchSpreadData, fetchSpreadDataFrom } from "../services/SpreadDataService";
import type { SpreadPoint } from "../definitions/SpreadPoint";

type Period = "1w" | "1m" | "3m" | "all";
type CatalogStatus = "idle" | "loading" | "ready" | "error";

const PERIOD_DAYS: Record<Exclude<Period, "all">, number> = {
  "1w": 7,
  "1m": 30,
  "3m": 90,
};

const PERIODS: { key: Period; label: string }[] = [
  { key: "1w", label: "1w" },
  { key: "1m", label: "1m" },
  { key: "3m", label: "3m" },
  { key: "all", label: "All" },
];

// Matches the 3 `--spread-compare-color-<n>` custom properties in base.css
// — see `themeColors` below, the only place that reads their actual values.
const COMPARE_SLOT_COUNT = 3;

// Same values as theme.css's defaults. Used when a `--spread-*-color` custom
// property reads back empty because theme.css (loaded by a `<link>` inside
// the shadow root) hasn't arrived yet when the data does — without this the
// line is drawn with `stroke=""` and never shows.
const FALLBACK_PRIMARY_COLOR = "#6fd7f5";
const FALLBACK_COMPARE_COLORS = ["#f5a623", "#a78bfa", "#f56f91"];

interface CompareSlot {
  symbol: string;
  name: string;
  color: string;
  data: SpreadPoint[];
}

export class LmaxWebSpreadChart extends LmaxBaseChartComponent {
  // See `LmaxBaseChartComponent.properties`' comment for why this is
  // explicitly typed as `PropertyDeclarations`.
  static properties: PropertyDeclarations = {
    src: { type: String },
    symbol: { type: String },
    venue: { type: String },
    interval: { type: String },
    compareVenue: { type: String, attribute: "compare-venue" },
    showtooltip: { type: Boolean },
    tooltipSpreadUnit: { type: String, attribute: "tooltipspreadunit" },
    styleHref: { type: String, attribute: "stylehref" },
    _data: { state: true },
    _period: { state: true },
    _compareSlots: { state: true },
    _catalogs: { state: true },
    _catalogStatus: { state: true },
    _openSlot: { state: true },
    _filter: { state: true },
    _symbolKey: { state: true },
    _hiddenSeries: { state: true },
    _reloading: { state: true },
  };

  declare src: string;
  declare symbol: string;
  declare venue: string;
  declare interval: string;
  declare compareVenue: string;
  declare showtooltip: boolean;
  /** Unit label after each tooltip value — `"pip"`, `"bps"`, `"{quote} pip"` (→ `"GBP pip"`). Empty shows the bare value. */
  declare tooltipSpreadUnit: string;
  declare styleHref: string;
  private declare _data: SpreadPoint[];
  private declare _period: Period;
  private declare _compareSlots: (CompareSlot | null)[];
  
  private declare _symbolKey: string | null;
  /** Instrument catalogs, keyed by venue — `venue` and `compare-venue` are cached separately since they're often different venues. */
  private declare _catalogs: Record<string, Instrument[]>;
  private declare _catalogStatus: Record<string, CatalogStatus>;
  /** Which dropdown is open: a compare slot index, `"primary"` for the symbol chip, or `null` for none. */
  private declare _openSlot: number | "primary" | null;
  private declare _filter: string;
  /** Series `id`s hidden via a legend-item click — excluded from `visibleSeries` (what actually gets plotted), but the legend itself still lists them, dimmed, so they can be clicked again to reshow. */
  private declare _hiddenSeries: Set<string>;
  /** A reload (e.g. a new primary instrument) is in flight while the previous chart stays on screen — see `load()`. */
  private declare _reloading: boolean;
  /** Increments per `load()` call, so a slow earlier response can't overwrite a newer one. */
  private loadId = 0;


  constructor() {
    super();
    this.src = "";
    // No instrument defaults — `venue`/`symbol` (or `src`) come from the
    // page's markup; see `load()` for what happens when they're missing.
    this.symbol = "";
    this.venue = "";
    this.interval = "1d";
    this.compareVenue = "";
    this.showtooltip = false;
    this.tooltipSpreadUnit = "";
    this.styleHref = "";
    this._data = [];
    this._period = "all";
    this._compareSlots = new Array(COMPARE_SLOT_COUNT).fill(null);
    this._catalogs = {};
    this._catalogStatus = {};
    this._openSlot = null;
    this._filter = "";
    this._symbolKey = null;
    this._hiddenSeries = new Set();
    this._reloading = false;
  }

  /** Venue the "+" compare slots pick from — `compare-venue`, or the chart's own `venue` when that isn't set. */
  private get compareVenueKey(): string {
    return this.compareVenue || this.venue;
  }

  /** The canonical symbol to fetch/compare against — see `_symbolKey`'s doc comment. */
  private get symbolKey(): string {
    return this._symbolKey ?? this.symbol.replace("/", "");
  }

  /**
   * Resolves this instance's plot colors from CSS custom properties (see
   * the class doc comment) — `base.css`'s `:root` defaults unless a
   * `[tagId="..."]` rule overrides them for this specific instance. D3
   * sets these as raw SVG attributes (`stroke`, ...), which the CSS cascade
   * doesn't reach, hence `readCssVar` rather than a plain `var()`.
   */
  private get themeColors(): { primary: string; compare: string[] } {
    return {
      primary: this.readCssVar("--spread-primary-color") || FALLBACK_PRIMARY_COLOR,
      compare: Array.from(
        { length: COMPARE_SLOT_COUNT },
        (_, i) => this.readCssVar(`--spread-compare-color-${i + 1}`) || FALLBACK_COMPARE_COLORS[i],
      ),
    };
  }


  connectedCallback(): void {
    super.connectedCallback();
    this.debugLog("connectedCallback", {
      src: this.src || "(none — fetching by venue/symbol)",
      venue: this.venue,
      symbol: this.symbol,
      interval: this.interval,
      compareVenue: this.compareVenue,
      showtooltip: this.showtooltip,
      tooltipSpreadUnit: this.tooltipSpreadUnit,
    });
    this.load();
  }


  private async load(): Promise<void> {
    const source = this.src ? { src: this.src } : { venue: this.venue, symbol: this.symbolKey, interval: this.interval };
    this.debugLog("load:start", source);
    const id = ++this.loadId;
    if (!this.src && !(this.venue && this.symbolKey)) {
      console.error(
        '[lmax-web-spread-chart] nothing to load — set `venue` and `symbol` (or `src`) on the element',
      );
      this.debugLog("load:error — missing venue/symbol", source);
      this._status = "error";
      return;
    }
    // A chart already on screen stays there, dimmed, until the new data
    // arrives — swapping the whole panel for the one-line loading message
    // collapsed it and made it jump back to full height on every
    // instrument change. Only the first load shows the loading state.
    const reloading = this._status === "ready";
    if (reloading) {
      this._reloading = true;
    } else {
      // Tear down the existing chart (and its ResizeObserver) *before* the
      // panel goes `hidden` below — otherwise the observer outlives the
      // now-zero-size container and fires `build()` with negative dimensions.
      this.cleanupChart?.();
      this.cleanupChart = undefined;
      this._status = "loading";
    }
    try {
      const data = this.src
        ? await fetchSpreadDataFrom(this.src)
        : await fetchSpreadData(this.venue, this.symbolKey, this.interval);
      if (id !== this.loadId) return;
      this._data = data;
      this._reloading = false;
      this._status = "ready";
      if (this._data.length === 0) {
        // Edge case: request succeeded but returned no points — nothing to
        // plot, distinct from the error path below.
        this.debugLog("load:success — empty dataset", source);
      } else {
        this.debugLog("load:success", { ...source, points: this._data.length });
      }
    } catch (error) {
      if (id !== this.loadId) return;
      console.error("[lmax-web-spread-chart] failed to load spread data:", error);
      this.debugLog("load:error", { ...source, error });
      this.cleanupChart?.();
      this.cleanupChart = undefined;
      this._reloading = false;
      this._status = "error";
    }
  }

  /**
   * Trims a series to the selected period, anchored to that series' own
   * latest point (not `Date.now()`) — so fixtures/older payloads still show
   * a sensible window regardless of when they're viewed.
   */
  private filterByPeriod(data: SpreadPoint[]): SpreadPoint[] {
    if (this._period === "all" || data.length === 0) return data;
    const latest = data[data.length - 1][0];
    const cutoff = latest - PERIOD_DAYS[this._period] * 24 * 60 * 60 * 1000;
    const filtered = data.filter(([t]) => t >= cutoff);
    if (filtered.length === 0) {
      // Edge case: the series' own data doesn't reach back as far as the
      // selected period (e.g. a fixture/new instrument with only a few
      // days of history and "3m" selected) — the line disappears entirely
      // rather than showing a partial window.
      this.debugLog("filterByPeriod — filtered out all points", { period: this._period, totalPoints: data.length });
    }
    return filtered;
  }

  /** The primary symbol's line plus every filled compare slot, period-filtered, ready for `chart.ts`. */
  private get series(): SpreadSeries[] {
    const primary: SpreadSeries = {
      id: "primary",
      label: this.symbolKey,
      color: this.themeColors.primary,
      data: this.filterByPeriod(this._data),
    };
    const compares: SpreadSeries[] = this._compareSlots
      .filter((slot): slot is CompareSlot => slot !== null)
      .map((slot) => ({
        id: slot.symbol,
        label: slot.symbol,
        color: slot.color,
        data: this.filterByPeriod(slot.data),
      }));
    return [primary, ...compares];
  }

  /** `series`, minus anything hidden via a legend-item click — what actually gets handed to `chart.ts`. */
  private get visibleSeries(): SpreadSeries[] {
    return this.series.filter((s) => !this._hiddenSeries.has(s.id));
  }

  protected updated(changed: PropertyValues): void {
    if (this._status === "ready") {
      const container = this.shadowRoot?.getElementById("chart");
      const rebuildTriggers =
        changed.has("_data") ||
        changed.has("tooltipSpreadUnit") ||
        changed.has("_status") ||
        changed.has("_period") ||
        changed.has("_compareSlots") ||
        changed.has("_hiddenSeries");
      if (container && rebuildTriggers) {
        this.debugLog("chart:rebuild", {
          changed: [...changed.keys()],
          series: this.visibleSeries.map((s) => ({ id: s.id, points: s.data.length })),
        });
        this.cleanupChart?.();
        this.cleanupChart = renderChart(container, this.visibleSeries, {
          showTooltip: this.showtooltip,
          tooltipSpreadUnit: this.tooltipSpreadUnit,
        });
      } else if (!container && rebuildTriggers) {
        // Edge case: status is "ready" but the shadow DOM hasn't painted the
        // `#chart` container yet — nothing to render into this pass.
        this.debugLog("chart:rebuild — skipped, no container", { changed: [...changed.keys()] });
      }
    }

    if (changed.has("_openSlot") && this._openSlot !== null) {
      this.shadowRoot?.querySelector<HTMLInputElement>(".compare-dropdown-search")?.focus();
    }
  }

  private setPeriod(period: Period): void {
    this.debugLog("period:change", { from: this._period, to: period });
    this._period = period;
  }

  /** Toggles a series' plotted-vs-hidden state — click a legend item to hide its line, click again to bring it back. */
  private toggleSeries(id: string): void {
    const next = new Set(this._hiddenSeries);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this._hiddenSeries = next;
    this.debugLog("legend:toggleSeries", { id, hidden: next.has(id) });
  }

  private toggleDropdown(target: number | "primary"): void {
    if (this._openSlot === target) {
      this.debugLog("dropdown:close", { target });
      this._openSlot = null;
      return;
    }
    this.debugLog("dropdown:open", { target });
    this._openSlot = target;
    this._filter = "";
    const venueKey = target === "primary" ? this.venue : this.compareVenueKey;
    if (!this._catalogStatus[venueKey] || this._catalogStatus[venueKey] === "idle") {
      this.loadCatalog(venueKey);
    }
  }

  private closeDropdown = (): void => {
    this._openSlot = null;
  };

  private async loadCatalog(venueKey: string): Promise<void> {
    this.debugLog("catalog:load:start", { venueKey });
    this._catalogStatus = { ...this._catalogStatus, [venueKey]: "loading" };
    try {
      const catalog = await fetchInstrumentCatalog(venueKey);
      this._catalogs = { ...this._catalogs, [venueKey]: catalog };
      this._catalogStatus = { ...this._catalogStatus, [venueKey]: "ready" };
      if (catalog.length === 0) {
        // Edge case: catalog fetch succeeded but the venue has no
        // instruments — the dropdown will render as permanently empty.
        this.debugLog("catalog:load:success — empty catalog", { venueKey });
      } else {
        this.debugLog("catalog:load:success", { venueKey, instruments: catalog.length });
      }
    } catch (error) {
      console.error(
        `[lmax-web-spread-chart] failed to load instrument catalog for venue "${venueKey}":`,
        error,
      );
      this.debugLog("catalog:load:error", { venueKey, error });
      this._catalogStatus = { ...this._catalogStatus, [venueKey]: "error" };
    }
  }

  /** Symbols to hide from a dropdown's list — everything already plotted elsewhere, excluding the slot being edited itself. */
  private usedSymbols(target: number | "primary"): Set<string> {
    const used = new Set<string>();
    if (target !== "primary") used.add(this.symbolKey);
    this._compareSlots.forEach((slot, i) => {
      if (slot && i !== target) used.add(slot.symbol);
    });
    return used;
  }

  private async selectInstrument(target: number | "primary", instrument: Instrument): Promise<void> {
    this.debugLog("instrument:select", { target, symbol: instrument.symbol, name: instrument.name });
    this._openSlot = null;

    if (target === "primary") {
      // `symbol` is the display label (e.g. "Arbitrum (USD)" — not always
      // slash-separated); `_symbolKey` is the canonical fetch key
      // (`instrument.symbol`, e.g. "ARBUSD"). Keep them separate rather
      // than deriving one from the other by string surgery.
      this.symbol = instrument.name;
      this._symbolKey = instrument.symbol;
      await this.load();
      return;
    }

    const index = target;
    const color = this.themeColors.compare[index % COMPARE_SLOT_COUNT];
    this._compareSlots = this._compareSlots.map((slot, i) =>
      i === index ? { symbol: instrument.symbol, name: instrument.name, color, data: [] } : slot,
    );

    this.debugLog("compare:load:start", { index, symbol: instrument.symbol, compareVenue: this.compareVenueKey });
    try {
      const data = await fetchSpreadData(this.compareVenueKey, instrument.symbol, this.interval);
      // The slot may have been removed or given a different instrument
      // while this was in flight — only fill it if it's still this one.
      this._compareSlots = this._compareSlots.map((slot, i) =>
        i === index && slot?.symbol === instrument.symbol ? { ...slot, data } : slot,
      );
      if (data.length === 0) {
        // Edge case: compare fetch succeeded but returned no points — the
        // chip stays, but its line never appears.
        this.debugLog("compare:load:success — empty dataset", { index, symbol: instrument.symbol });
      } else {
        this.debugLog("compare:load:success", { index, symbol: instrument.symbol, points: data.length });
      }
    } catch (error) {
      console.error(
        `[lmax-web-spread-chart] failed to load compare data for ${instrument.symbol}:`,
        error,
      );
      // Edge case: the fetch failed after the chip/slot was already shown
      // optimistically above — roll the slot back to empty rather than
      // leaving a chip with permanently-missing data.
      this.debugLog("compare:load:error — slot reverted", { index, symbol: instrument.symbol, error });
      this._compareSlots = this._compareSlots.map((slot, i) =>
        i === index && slot?.symbol === instrument.symbol ? null : slot,
      );
    }
  }

  private removeCompareSlot(index: number): void {
    this.debugLog("compare:remove", { index });
    this._compareSlots = this._compareSlots.map((slot, i) => (i === index ? null : slot));
  }

  private onFilterInput(event: Event): void {
    this._filter = (event.target as HTMLInputElement).value;
  }

  private renderDropdown(target: number | "primary") {
    const venueKey = target === "primary" ? this.venue : this.compareVenueKey;
    const status = this._catalogStatus[venueKey] ?? "idle";

    if (status === "idle" || status === "loading") {
      return html`
        <div class="dropdown-backdrop" @click=${this.closeDropdown}></div>
        <div class="compare-dropdown">
          <div class="compare-dropdown-status">Loading instruments…</div>
        </div>
      `;
    }
    if (status === "error") {
      return html`
        <div class="dropdown-backdrop" @click=${this.closeDropdown}></div>
        <div class="compare-dropdown">
          <div class="compare-dropdown-status">Couldn't load instruments.</div>
        </div>
      `;
    }

    const used = this.usedSymbols(target);
    const filter = this._filter.trim().toUpperCase();
    const catalog = this._catalogs[venueKey] ?? [];
    const items = catalog.filter(
      (i) =>
        !used.has(i.symbol) &&
        (filter === "" || i.symbol.includes(filter) || i.name.toUpperCase().includes(filter)),
    );

    const groups = new Map<string, Instrument[]>();
    for (const item of items) {
      const list = groups.get(item.group) ?? [];
      list.push(item);
      groups.set(item.group, list);
    }

    return html`
      <div class="dropdown-backdrop" @click=${this.closeDropdown}></div>
      <div class="compare-dropdown" role="listbox" aria-label="Choose an instrument">
        <input
          class="compare-dropdown-search"
          type="text"
          placeholder="Search instruments…"
          .value=${this._filter}
          @input=${this.onFilterInput}
          @click=${(e: Event) => e.stopPropagation()}
        />
        ${items.length === 0
          ? html`<div class="compare-dropdown-status">No matching instruments.</div>`
          : Array.from(groups.entries()).map(
              ([group, groupItems]) => html`
                <div class="compare-dropdown-group">${group}</div>
                ${groupItems.map(
                  (item) => html`
                    <button
                      class="compare-dropdown-item"
                      role="option"
                      type="button"
                      @click=${() => this.selectInstrument(target, item)}
                    >
                      <span class="compare-dropdown-symbol">${item.symbol}</span>
                      <span class="compare-dropdown-name">${item.name}</span>
                    </button>
                  `,
                )}
              `,
            )}
      </div>
    `;
  }
  render() {
    return html`
      ${this.styleHref ? html`<link rel="stylesheet" href=${this.styleHref} />` : nothing}
      ${this.renderStatus(
        "Loading spread data…",
        "Couldn't load spread data. Please try again shortly.",
      )}
      <section aria-label="${this.symbol} spread average" ?hidden=${this._status !== "ready"}>
        <div class="toolbar">
          <div class="dropdown-anchor">
            <button
              class="symbol-chip"
              type="button"
              style="--chip-color: ${this.themeColors.primary}"
              aria-label="Change symbol"
              aria-expanded=${this._openSlot === "primary"}
              @click=${() => this.toggleDropdown("primary")}
            >
              <span class="chip-dot"></span>
              <span class="symbol-chip-label">${this.symbol}</span>
            </button>
            ${this._openSlot === "primary" ? this.renderDropdown("primary") : nothing}
          </div>
          ${this._compareSlots.map((slot, i) =>
            slot
              ? html`
                  <div class="compare-chip" style="--chip-color: ${slot.color}">
                    <span class="chip-dot"></span>
                    <span>${slot.symbol}</span>
                    <button
                      class="chip-remove"
                      type="button"
                      aria-label="Remove ${slot.symbol}"
                      @click=${() => this.removeCompareSlot(i)}
                    >
                      ×
                    </button>
                  </div>
                `
              : html`
                  <div class="dropdown-anchor">
                    <button
                      class="add-btn"
                      type="button"
                      aria-label="Add comparison instrument"
                      aria-expanded=${this._openSlot === i}
                      @click=${() => this.toggleDropdown(i)}
                    >
                      +
                    </button>
                    ${this._openSlot === i ? this.renderDropdown(i) : nothing}
                  </div>
                `,
          )}
          <div class="spacer"></div>
          <div class="period-group" role="group" aria-label="Time range">
            ${PERIODS.map(
              ({ key, label }) => html`
                <button
                  class="period-btn ${key === this._period ? "active" : ""}"
                  type="button"
                  aria-pressed=${key === this._period}
                  @click=${() => this.setPeriod(key)}
                >
                  ${label}
                </button>
              `,
            )}
          </div>
        </div>
        <div class="chart-wrap" aria-busy=${this._reloading}>
          <div class="chart-container" id="chart"></div>
          <div class="chart-tooltip" role="status" aria-live="polite"></div>
        </div>
        <div class="legend">
          ${this.series.map(
            (s) => html`
              <button
                type="button"
                class="legend-item ${this._hiddenSeries.has(s.id) ? "legend-item--hidden" : ""}"
                aria-pressed=${!this._hiddenSeries.has(s.id)}
                aria-label="Toggle ${s.label} ${this._hiddenSeries.has(s.id) ? "on" : "off"}"
                @click=${() => this.toggleSeries(s.id)}
              >
                <span class="legend-swatch" style="background:${s.color}"></span>
                <span>${s.label}</span>
              </button>
            `,
          )}
        </div>
      </section>
      ${this.cssIncludesResolved}
    `;
  }
}

customElements.define("lmax-web-spread-chart", LmaxWebSpreadChart);

/**
 * Host pages often keep the chart in a `<template>` and only clone it in
 * when a popup opens, so the chart's own fetch-on-connect doesn't start
 * until the click — and the user waits on the network. This starts the
 * default instrument's request for every spread chart sitting in a
 * `<template>` as soon as the page loads; `SpreadDataService` shares the
 * in-flight/finished request with the chart once it's created. Charts
 * already in the live DOM need nothing — they fetch on connect anyway.
 */
function prefetchTemplateSpreadCharts(): void {
  for (const template of document.querySelectorAll("template")) {
    for (const chart of template.content.querySelectorAll("lmax-web-spread-chart")) {
      // Only what the template itself states — a template that leaves the
      // instrument out isn't prefetched, and the chart fetches on open.
      const src = chart.getAttribute("src");
      const venue = chart.getAttribute("venue");
      const symbol = chart.getAttribute("symbol")?.replace("/", "");
      const interval = chart.getAttribute("interval") ?? undefined;
      if (!src && !(venue && symbol)) continue;
      try {
        // Errors are swallowed here: the failed request is evicted from the
        // cache, so the chart retries and reports it when it's opened.
        (src ? fetchSpreadDataFrom(src) : fetchSpreadData(venue!, symbol!, interval)).catch(() => {});
      } catch {
        // `fetchSpreadData` throws synchronously when the venue has no chart
        // endpoint configured — again, the chart reports that itself.
      }
    }
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", prefetchTemplateSpreadCharts, { once: true });
} else {
  prefetchTemplateSpreadCharts();
}

declare global {
  interface HTMLElementTagNameMap {
    "lmax-web-spread-chart": LmaxWebSpreadChart;
  }
}
